import {makeAutoObservable, observable, runInAction} from "mobx";
import {ViewController} from "data/types/structure";
import {injectable, inject} from "inversify";
import {Countdown, type IDateTimer, wait} from "data/utils";
import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IContestsStore} from "data/stores/contests/contestsStore";
import {ContestsUtils} from "data/utils/contest.utils";
import type {IChecksumStore} from "data/stores/checksum/checksum.store";

interface IParams {
	contestId: number;
	onComplete?: () => void;
}

export interface IContestLocksStringController extends ViewController<IParams> {
	i18n: ILocalizationStore;

	get timerString(): string | null;
}

@injectable()
export class ContestLocksStringController implements IContestLocksStringController {
	private _contestId?: number;
	private _onComplete?: () => void;
	private _timer?: Countdown;
	@observable private _date: IDateTimer | null = null;

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.ChecksumStore) public readonly _checksumsStore: IChecksumStore,
		@inject(Bindings.ContestsStore) public readonly _contestsStore: IContestsStore
	) {
		makeAutoObservable(this);
	}

	init(param: IParams) {
		this._contestId = param.contestId;
		this._onComplete = param.onComplete;
		this.initTimer();
	}

	onChange(param: IParams) {
		this.init(param);
	}

	get timerString(): string | null {
		if (!this._date) {
			return "00:00";
		}
		const {days, minutes, hours, seconds} = this._date;

		if (days) {
			return `${days}d:${hours}h`;
		}

		if (hours) {
			return `${hours}h:${minutes}m`;
		}

		return `${minutes}m:${seconds}s`;
	}

	private initTimer = () => {
		this._date = null;
		if (!this.contest) {
			return;
		}

		this._timer?.clear();
		this._timer = ContestsUtils.initLockTimer(this.contest);
		if (!this._timer) {
			this._onComplete?.();
			return;
		}

		this._timer
			.onTick((days, hours, minutes, seconds) => {
				runInAction(() => {
					this._date = {
						days,
						hours,
						minutes,
						seconds,
					};
				});
			})
			.onComplete(() => {
				this._date = null;
				this._onComplete?.();
				void wait(1000).then(() => {
					void this._checksumsStore.fetchChecksums();
				});
			})
			.run();
	};

	dispose() {
		this._timer?.clear();
	}

	private get contest() {
		if (!this._contestId) {
			return;
		}
		return this._contestsStore.getContestById(this._contestId);
	}
}
